import type { ReactNode } from 'react';
import { NoConnectionScreen } from '@/shared/error-screens';
import { offlineStore, type OfflineStore } from './offline-store';
import { useOfflineState } from './use-offline-state';

/**
 * EU-711 — o ponto único de "sem internet" do App Candidato (FR-002).
 *
 * Fica ACIMA do router, então cobre tudo: conclusão de etapa, listagens,
 * navegação, perfil, envio de arquivo. Antes, a decisão offline-vs-servidor
 * existia só no `defaultErrorComponent` do router — ou seja, só quando um LOADER
 * de rota lançava. A falha reportada no card é uma MUTATION dentro da página
 * (`POST /modules/:id/responses`), que nunca passava por lá; o candidato só via
 * um toast que sumia.
 *
 * **A árvore é escondida por CSS, nunca desmontada** — e isso é o núcleo da
 * feature, não um detalhe de implementação:
 *
 * - a etapa SurveyJS guarda o preenchimento no estado do componente. Desmontar
 *   zera as respostas, que é exatamente o que FR-007 proíbe;
 * - a etapa arma `useBlocker` enquanto está suja. Navegar para uma ROTA de erro
 *   traria o diálogo "Tem certeza? Se sair agora, as informações preenchidas não
 *   serão enviadas." por cima da tela de erro;
 * - com a árvore viva, "Tentar novamente" (OFF-003) e o "voltar" do navegador
 *   (OFF-005) devolvem o candidato ao módulo no mesmo ponto de graça — não há
 *   estado a restaurar porque nada foi perdido.
 */
export function OfflineGate({
  children,
  store = offlineStore,
}: {
  children: ReactNode;
  store?: OfflineStore;
}) {
  const { offline, retry } = useOfflineState(store);

  // O ciclo de vida do store NÃO é do gate: quem liga é `installOfflineDetector`
  // no boot, uma vez. Um `start`/`stop` no effect daria DUAS donas ao mesmo
  // singleton e, no ciclo mount→unmount→mount do StrictMode, o `stop` largaria
  // a entrada de histórico órfã — o "voltar" do navegador pararia de fechar a
  // tela. O gate só LÊ.

  return (
    <>
      {/*
        `hidden` + `aria-hidden` tiram a árvore da tela, da ordem de foco e dos
        leitores de tela — sem desmontá-la. Um `display: none` do container não
        desmonta nem reinicializa nada dentro dele.
      */}
      <div data-testid="offline-gate-app-shell" hidden={offline} aria-hidden={offline || undefined}>
        {children}
      </div>
      {offline ? <NoConnectionScreen onRetry={() => void retry()} /> : null}
    </>
  );
}
